<template>
  <div class="delta-chart">
    <div class="delta-head">
      <h3>Evolução de Delta</h3>
      <label class="delta-check">
        <input type="checkbox" v-model="includeSpot" />
        Spot
      </label>
      <label class="delta-check">
        <input type="checkbox" v-model="includeFutures" />
        Futuros
      </label>
    </div>
    <p v-if="!hasSelection" class="delta-empty">Selecione spot, futuros ou os dois.</p>
    <div v-else class="delta-plot">
      <apexchart
        :key="chartKey"
        height="100%"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
  </div>
</template>

<script>
import { defaultChartOptions } from '@/utils/globalApexConfig';

export default {
  name: 'EvolManualsGridChart',
  props: {
    timeSeries: { type: Array, required: true },
    aggLabels:  { type: Array, required: true },
    step:       { type: Number, default: 2000 },
  },
  data() {
    return {
      includeSpot: true,
      includeFutures: true,
    };
  },
  computed: {
    hasSelection() {
      return this.includeSpot || this.includeFutures;
    },
    chartKey() {
      const last = this.parsedTimeSeries[this.parsedTimeSeries.length - 1];
      return `delta-${this.includeSpot}-${this.includeFutures}-${this.parsedTimeSeries.length}-${last?.longsTotalQty ?? 0}-${last?.shortsTotalQty ?? 0}`;
    },
    parsedTimeSeries() {
      if (!this.hasSelection) return [];
      return this.timeSeries.map(({ index, m }) => {
        const snapshot = m?.manualsSnapshot || {};
        const currentPrice = m?.currentPrice || 0;

        const spotShorts = this.includeSpot
          ? (snapshot.spotShorts || []).map(s => ({ price: Number(s.p), qty: Number(s.q) }))
          : [];
        const futuresShorts = this.includeFutures
          ? (snapshot.futuresShorts || []).map(s => ({ price: Number(s.p), qty: Number(s.q) }))
          : [];
        const shortsList = [...spotShorts, ...futuresShorts];

        const spotLongs = this.includeSpot
          ? (snapshot.spotLongs || []).map(l => ({ price: Number(l.p), qty: Number(l.q) }))
          : [];
        const futuresLongs = this.includeFutures
          ? (snapshot.futuresLongs || []).map(l => ({ price: Number(l.p), qty: Number(l.q) }))
          : [];
        const longsList = [...spotLongs, ...futuresLongs];

        const shortsTotalQty = shortsList.reduce((acc, s) => acc + s.qty, 0);
        const shortsNotional = shortsList.reduce((acc, s) => acc + (s.qty * s.price), 0);
        const shortsAvgPrice = shortsTotalQty > 0 ? shortsNotional / shortsTotalQty : 0;

        const longsTotalQty = longsList.reduce((acc, l) => acc + l.qty, 0);
        const longsNotional = longsList.reduce((acc, l) => acc + (l.qty * l.price), 0);
        const longsAvgPrice = longsTotalQty > 0 ? longsNotional / longsTotalQty : 0;

        const allPositions = [...shortsList, ...longsList];
        
        let rows = [];
        if (allPositions.length > 0) {
          const prices = allPositions.map(p => p.price);
          const minPrice = Math.floor(Math.min(...prices) / this.step) * this.step;
          const maxPrice = Math.floor(Math.max(...prices) / this.step) * this.step + this.step;

          const bins = [];
          for (let p = minPrice; p <= maxPrice; p += this.step) {
            bins.push(p);
          }

          for (let i = 0; i < bins.length - 1; i++) {
            const low = bins[i];
            const high = bins[i + 1];
            const midTarget = (low + high) / 2.0;

            const shortQtyBin = shortsList
              .filter(s => s.price >= low && s.price < high)
              .reduce((acc, s) => acc + s.qty, 0);

            const longQtyBin = longsList
              .filter(l => l.price >= low && l.price < high)
              .reduce((acc, l) => acc + l.qty, 0);

            const shortProfitable = shortsList
              .filter(s => s.price > midTarget)
              .reduce((acc, s) => acc + (s.qty * (s.price - midTarget)), 0);

            const longProfitable = longsList
              .filter(l => l.price < midTarget)
              .reduce((acc, l) => acc + (l.qty * (midTarget - l.price)), 0);

            const shortTotalPnL = shortsTotalQty * (shortsAvgPrice - midTarget);
            const longTotalPnL = longsTotalQty * (midTarget - longsAvgPrice);
            const netTotalPnL = shortTotalPnL + longTotalPnL;

            rows.push({
              faixa: `$${Math.floor(low / 1000)}k - $${Math.floor(high / 1000)}k`,
              low,
              high,
              midTarget,
              shortQty: shortQtyBin,
              shortProfit: shortProfitable,
              shortPnL: shortTotalPnL,
              longQty: longQtyBin,
              longProfit: longProfitable,
              longPnL: longTotalPnL,
              netPnL: netTotalPnL
            });
          }

          rows.reverse();
        }

        const totalShortProfitAtPrice = shortsList
          .filter(s => s.price > currentPrice)
          .reduce((acc, s) => acc + (s.qty * (s.price - currentPrice)), 0);

        const totalLongProfitAtPrice = longsList
          .filter(l => l.price < currentPrice)
          .reduce((acc, l) => acc + (l.qty * (currentPrice - l.price)), 0);

        const totalShortPnLAtPrice = shortsTotalQty * (shortsAvgPrice - currentPrice);
        const totalLongPnLAtPrice = longsTotalQty * (currentPrice - longsAvgPrice);
        const totalNetPnLAtPrice = totalShortPnLAtPrice + totalLongPnLAtPrice;

        return {
          index,
          currentPrice,
          shortsAvgPrice,
          longsAvgPrice,
          shortsTotalQty,
          longsTotalQty,
          totalShortProfit: totalShortProfitAtPrice,
          totalLongProfit: totalLongProfitAtPrice,
          totalShortPnL: totalShortPnLAtPrice,
          totalLongPnL: totalLongPnLAtPrice,
          totalNetPnL: totalNetPnLAtPrice,
          rows
        };
      });
    },

    chartSeries() {
      const longQtyArray = [];
      const shortQtyArray = [];
      const deltaQtyArray = [];

      this.parsedTimeSeries.forEach((item) => {
        const i = item.index;
        const longQty = Number(item.longsTotalQty.toFixed(4));
        const shortQty = Number(item.shortsTotalQty.toFixed(4));
        longQtyArray.push([i, longQty]);
        shortQtyArray.push([i, shortQty]);
        deltaQtyArray.push([i, Number((longQty - shortQty).toFixed(4))]);
      });

      return [
        { name: 'Long Qty',  type: 'line', data: longQtyArray },
        { name: 'Short Qty', type: 'line', data: shortQtyArray },
        { name: 'Delta Qty', type: 'line', data: deltaQtyArray },
      ];
    },

    chartOptions() {
      const self = this;
      return {
        ...defaultChartOptions,
        colors: ['#26a69a', '#ef5350', '#e3b341'],
        grid: {
          borderColor: '#333',
          padding: { right: 16, left: 15 }
        },
        xaxis: { type: 'numeric', labels: { formatter: this.xAxisFormatter } },
        yaxis: {
          title: { text: 'Qtd (BTC)' },
          labels: { show: true, formatter: v => v != null ? Number(v).toFixed(4) : '0' },
        },
        tooltip: {
          shared: false,
          intersect: false,
          custom: function({ dataPointIndex }) {
            return self.generateTooltipHtml(dataPointIndex);
          }
        }
      };
    },
  },
  methods: {
    xAxisFormatter(val) {
      return this.aggLabels[Math.floor(val)] || '';
    },
    generateTooltipHtml(dataPointIndex) {
      const item = this.parsedTimeSeries[dataPointIndex];
      if (!item) return '';

      const label = this.aggLabels[dataPointIndex] || '';
      const currentPriceFormatted = item.currentPrice
        ? `$${item.currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : '$0.00';
      const pmpShortsFormatted = item.shortsAvgPrice
        ? `$${item.shortsAvgPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : '$0.00';
      const pmpLongsFormatted = item.longsAvgPrice
        ? `$${item.longsAvgPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : '$0.00';
      const shortsQtyFormatted = item.shortsTotalQty != null ? item.shortsTotalQty.toFixed(4) : '0.0000';
      const longsQtyFormatted = item.longsTotalQty != null ? item.longsTotalQty.toFixed(4) : '0.0000';
      const deltaQty = (item.longsTotalQty || 0) - (item.shortsTotalQty || 0);
      const deltaQtyFormatted = deltaQty.toFixed(4);
      const deltaQtyColor = deltaQty > 0 ? '#26a69a' : deltaQty < 0 ? '#ef5350' : '#e3b341';

      const fmtCurrency = (v) => {
        if (v == null) return '$0.00';
        const formatted = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return v < 0 ? `-$${formatted}` : `$${formatted}`;
      };

      const deltaPnL = item.totalNetPnL || 0;
      const deltaPnLColor = deltaPnL > 0 ? '#26a69a' : deltaPnL < 0 ? '#ef5350' : '#e3b341';

      let rowsHtml = '';
      (item.rows || []).forEach(r => {
        const shortQtyStyle = r.shortQty > 0
          ? 'background-color: rgba(239, 83, 80, 0.25); color: #ef5350; font-weight: bold;'
          : 'color: #888;';
        const longQtyStyle = r.longQty > 0
          ? 'background-color: rgba(38, 166, 154, 0.25); color: #26a69a; font-weight: bold;'
          : 'color: #888;';

        const shortPnlColor = r.shortPnL > 0 ? '#26a69a' : r.shortPnL < 0 ? '#ef5350' : '#888';
        const longPnlColor = r.longPnL > 0 ? '#26a69a' : r.longPnL < 0 ? '#ef5350' : '#888';
        const netPnlColor = r.netPnL > 0 ? '#26a69a' : r.netPnL < 0 ? '#ef5350' : '#888';

        rowsHtml += `
          <tr style="border-bottom: 1px solid #333;">
            <td style="padding: 6px 16px; text-align: left; font-weight: bold; color: #ddd; white-space: nowrap;">${r.faixa}</td>
            <td style="padding: 6px 16px; text-align: right; white-space: nowrap; ${shortQtyStyle}">${r.shortQty.toFixed(4)}</td>
            <td style="padding: 6px 16px; text-align: right; color: #ccc; white-space: nowrap;">${fmtCurrency(r.shortProfit)}</td>
            <td style="padding: 6px 16px; text-align: right; color: ${shortPnlColor}; font-weight: bold; white-space: nowrap;">${fmtCurrency(r.shortPnL)}</td>
            <td style="padding: 6px 16px; text-align: right; white-space: nowrap; ${longQtyStyle}">${r.longQty.toFixed(4)}</td>
            <td style="padding: 6px 16px; text-align: right; color: #ccc; white-space: nowrap;">${fmtCurrency(r.longProfit)}</td>
            <td style="padding: 6px 16px; text-align: right; color: ${longPnlColor}; font-weight: bold; white-space: nowrap;">${fmtCurrency(r.longPnL)}</td>
            <td style="padding: 6px 18px 6px 16px; text-align: right; color: ${netPnlColor}; font-weight: bold; white-space: nowrap;">${fmtCurrency(r.netPnL)}</td>
          </tr>
        `;
      });

      return `
        <div style="background-color: #1e1e1e; color: #fff; padding: 14px 20px; border-radius: 6px; border: 1px solid #444; font-size: 11px; box-shadow: 0 4px 16px rgba(0,0,0,0.7); pointer-events: none; box-sizing: border-box; width: max-content; min-width: 980px; max-width: 1200px;">
          <div style="margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #444; display: flex; justify-content: space-between; align-items: center; gap: 16px; box-sizing: border-box;">
            <div>
              <span style="font-weight: bold; color: #38bdf8;">📅 ${label}</span>
            </div>
            <div style="white-space: nowrap;">
              <span>💰 Preço BTC: <b>${currentPriceFormatted}</b></span> | 
              <span style="color: #ef5350;">📉 PMP Shorts: <b>${pmpShortsFormatted}</b> (Qtd: <b>${shortsQtyFormatted}</b>)</span> | 
              <span style="color: #26a69a;">📈 PMP Longs: <b>${pmpLongsFormatted}</b> (Qtd: <b>${longsQtyFormatted}</b>)</span> | 
              <span style="color: ${deltaQtyColor};">Δ Delta: <b>${deltaQtyFormatted}</b></span> | 
              <span style="color: ${deltaPnLColor};">Δ PnL: <b>${fmtCurrency(deltaPnL)}</b></span>
            </div>
          </div>
          <table style="width: 100%; border-collapse: collapse; font-family: monospace; box-sizing: border-box; table-layout: auto;">
            <thead>
              <tr style="background-color: #2b2b2b; color: #fff;">
                <th style="padding: 6px 16px; text-align: left; white-space: nowrap;">faixa</th>
                <th style="padding: 6px 16px; text-align: right; white-space: nowrap;">Short Qty</th>
                <th style="padding: 6px 16px; text-align: right; white-space: nowrap;">Short Profit</th>
                <th style="padding: 6px 16px; text-align: right; white-space: nowrap;">Short PnL</th>
                <th style="padding: 6px 16px; text-align: right; white-space: nowrap;">Long Qty</th>
                <th style="padding: 6px 16px; text-align: right; white-space: nowrap;">Long Profit</th>
                <th style="padding: 6px 16px; text-align: right; white-space: nowrap;">Long PnL</th>
                <th style="padding: 6px 18px 6px 16px; text-align: right; white-space: nowrap;">Net PnL</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml || '<tr><td colspan="8" style="padding: 8px; text-align: center;">Nenhum dado de snapshot</td></tr>'}
            </tbody>
          </table>
        </div>
      `;
    }
  }
};
</script>

<style scoped>
.delta-chart {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.delta-head {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}

.delta-head h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #f1f1f1;
}

.delta-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--text-color, #f1f1f1);
  cursor: pointer;
}

.delta-check input[type="checkbox"] {
  margin: 0;
}

.delta-plot {
  flex: 1;
  min-height: 0;
}

.delta-empty {
  margin: 0;
  opacity: 0.7;
}
</style>
